var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import _, { flatMap, intersection } from 'lodash';
import { compMathMethodDataType, compMathMethodExperimentalDesign, compMathMethodModels } from './compMathMethod';
import { dsm5 } from './dsm5';
import { rdocConstructs } from './rdocConstructs';
import { symptomatology } from './symptomatology';
import { unitOfAnalysis } from './unitOfAnalysis';
export var tagAxisIds = [
    'rdocConstructs',
    'unitOfAnalysis',
    'dsm5',
    'compMathMethodExperimentalDesign',
    'compMathMethodDataType',
    'compMathMethodModels',
    'symptomatology'
];
export var tagAxes = Object.freeze([
    rdocConstructs,
    unitOfAnalysis,
    dsm5,
    symptomatology,
    compMathMethodDataType,
    compMathMethodExperimentalDesign,
    compMathMethodModels
]);
export var tagAxesObject = Object.freeze({
    rdocConstructs: rdocConstructs,
    unitOfAnalysis: unitOfAnalysis,
    dsm5: dsm5,
    symptomatology: symptomatology,
    compMathMethodDataType: compMathMethodDataType,
    compMathMethodExperimentalDesign: compMathMethodExperimentalDesign,
    compMathMethodModels: compMathMethodModels
});
var getIdTagMap = function (tag, tagAxisId, depth, parentTagId, ancestorTagId) {
    var _a;
    if (depth === void 0) { depth = 1; }
    var idTagMap = __assign((_a = {}, _a[tag.id] = __assign(__assign({}, tag), { tagAxisId: tagAxisId,
        depth: depth,
        parentTagId: parentTagId, ancestorTagId: ancestorTagId !== null && ancestorTagId !== void 0 ? ancestorTagId : tag.id }), _a), (tag.children
        ? _.assign.apply(_, __spreadArrays([{}], tag.children.map(function (c) {
            return getIdTagMap(c, tagAxisId, depth + 1, tag.id, ancestorTagId !== null && ancestorTagId !== void 0 ? ancestorTagId : tag.id);
        }))) : {}));
    return idTagMap;
};
var getTagAxisMap = function (tagAxis) {
    var tagAxisIdTagMap = _.assign.apply(_, __spreadArrays([{}], tagAxis.tags.map(function (tag) { return getIdTagMap(tag, tagAxis.id); })));
    return tagAxisIdTagMap;
};
export var tagAxesMap = Object.freeze({
    rdocConstructs: getTagAxisMap(rdocConstructs),
    unitOfAnalysis: getTagAxisMap(unitOfAnalysis),
    dsm5: getTagAxisMap(dsm5),
    symptomatology: getTagAxisMap(symptomatology),
    compMathMethodDataType: getTagAxisMap(compMathMethodDataType),
    compMathMethodExperimentalDesign: getTagAxisMap(compMathMethodExperimentalDesign),
    compMathMethodModels: getTagAxisMap(compMathMethodModels)
});
export var tagMap = Object.freeze(_.assign.apply(_, __spreadArrays([{}], Object.values(tagAxesMap))));
var getAllChildrens = function (tag) {
    var detailedTags = tag.children
        ? _.flatten(tag.children.map(function (cTag) { return getAllChildrens(cTag); }))
        : [];
    var currentDetailedTag = tagMap[tag.id];
    if (!currentDetailedTag) {
        throw new Error();
    }
    return __spreadArrays([currentDetailedTag], detailedTags);
};
var detailingTagAxis = function (tagAxis) {
    return _.flatten(tagAxis.tags.map(function (tag) { return getAllChildrens(tag); }));
};
export var detailedTagAxes = Object.freeze({
    rdocConstructs: detailingTagAxis(rdocConstructs),
    unitOfAnalysis: detailingTagAxis(unitOfAnalysis),
    dsm5: detailingTagAxis(dsm5),
    symptomatology: detailingTagAxis(symptomatology),
    compMathMethodDataType: detailingTagAxis(compMathMethodDataType),
    compMathMethodExperimentalDesign: detailingTagAxis(compMathMethodExperimentalDesign),
    compMathMethodModels: detailingTagAxis(compMathMethodModels)
});
export var getAllParentsDetailedTag = function (detailedTag, excludeOwnTag) {
    if (detailedTag.parentTagId) {
        var parentDetailedTag = tagMap[detailedTag.parentTagId];
        var parentsTagIds = parentDetailedTag
            ? getAllParentsDetailedTag(parentDetailedTag)
            : [];
        return __spreadArrays(parentsTagIds, (excludeOwnTag === true ? [] : [detailedTag.id]));
    }
    return excludeOwnTag === true ? [] : [detailedTag.id];
};
export var getDetailedTags = function (ids) {
    return _.compact(ids.map(function (id) { return tagMap[id]; }));
};
export var getAllParentsTagIds = function (ids, excludeOwnTag) {
    return intersection(flatMap(getDetailedTags(ids), function (detailedTag) {
        return getAllParentsDetailedTag(detailedTag, excludeOwnTag);
    }));
};
export var isSelectedChildrensFromDetailedTags = function (detailedTags, tagId) {
    return detailedTags
        .map(function (detailedTag) {
        var parentsTagIds = getAllParentsDetailedTag(detailedTag);
        if (parentsTagIds.includes(tagId)) {
            return true;
        }
        return false;
    })
        .includes(true);
};
export var excludeTags = function (tags, ids) {
    return _.compact(tags.map(function (tag) {
        if (ids.includes(tag.name)) {
            return null;
        }
        if (tag.children) {
            var excludedChildren = excludeTags(tag.children, ids);
            if (tag.name === 'Others' && _.isEmpty(excludedChildren)) {
                return null;
            }
            return __assign(__assign({}, tag), { children: excludedChildren });
        }
        return tag;
    }));
};
export var flatOtherTags = function (tags) {
    return _.flatten(tags.map(function (tag) {
        if (tag.name === 'Others' && tag.children) {
            return tag.children;
        }
        return tag;
    }));
};
export var removeParentsTagIds = function (ids) {
    var tags = getDetailedTags(ids);
    var allParentsTagIds = _.flatten(tags.map(function (tag) { return getAllParentsDetailedTag(tag, true); }));
    var removedParentsIds = ids.filter(function (id) { return !allParentsTagIds.includes(id); });
    return removedParentsIds;
};
export var extractDetailedTag = function (tagAxisId, name) {
    var _a;
    var tagArray = Object.keys(tagAxesMap[tagAxisId]);
    for (var i = 0; i < tagArray.length; i += 1) {
        var key = tagArray[i];
        if (((_a = tagAxesMap[tagAxisId][key]) === null || _a === void 0 ? void 0 : _a.name) === name) {
            return tagAxesMap[tagAxisId][key];
        }
    }
    return undefined;
};
export var arrangementTags = function (tagAxisId, ids) {
    var unspecifiedTag = extractDetailedTag(tagAxisId, 'Unspecified');
    if (!unspecifiedTag) {
        console.error('Unspecified tags no exists');
        throw new Error('Unspecified tags no exists');
    }
    if (_.isEmpty(ids)) {
        return [unspecifiedTag.id];
    }
    var tags = getDetailedTags(ids);
    var removedTags = _.remove(_.clone(tags.map(function (tag) { return tag.id; })), function (id) { return id !== unspecifiedTag.id; });
    return removeParentsTagIds(removedTags);
};
export var removedUnspecifiedTagIds = function (tagAxisId, ids) {
    var unspecifiedTag = extractDetailedTag(tagAxisId, 'Unspecified');
    if (!unspecifiedTag) {
        console.error('Unspecified tags no exists');
        throw new Error('Unspecified tags no exists');
    }
    var tags = getDetailedTags(ids);
    var removedTags = _.remove(_.clone(tags.map(function (tag) { return tag.id; })), function (id) { return id !== unspecifiedTag.id; });
    return removedTags;
};
export var getFamilyTree = function (currentDetailedTag) {
    if (currentDetailedTag.parentTagId) {
        var parentDetailedTag = tagMap[currentDetailedTag.parentTagId];
        if (!parentDetailedTag)
            throw new Error('想定外');
        return __spreadArrays([currentDetailedTag], getFamilyTree(parentDetailedTag)).reverse();
    }
    return [currentDetailedTag];
};
export var deprecatedTagIds = ['F2kGnR', 'uJ5cwd', 'k8Wdp3'];
